/* eslint-disable @typescript-eslint/no-explicit-any */
import { FilterField, labelId } from "kontrolEdge/helpers/advancedFilters";
import { SupportTicket } from "./RequestData";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import React from "react";

export const statusText = (s: any): string =>
  typeof s === "string"
    ? s
    : s?.label_for_customers || s?.label || s?.name || "Unknown";

export const mapStatusToVariant = (
  statusRaw: any
): "success" | "neutral" | "error" | "warning" | "default" => {
  const s = statusText(statusRaw).toLowerCase();
  if (s === "resolved") return "success";
  if (s === "rejected") return "error";
  if (s === "pending") return "warning";
  if (s === "canceled") return "default";
  // unknown as neutral
  return "neutral";
};

export const priorityLabel = (
  p?: number
): "Low" | "Medium" | "High" | "Urgent" | "N/A" => {
  switch (Number(p)) {
    case 4:
      return "Low";
    case 3:
      return "Medium";
    case 2:
      return "High";
    case 1:
    case 10000:
      return "Urgent";
    default:
      return "N/A";
  }
};

type Tone = { bg: string; text: string; border?: string };
const TYPE_TONE_MAP: Record<string, Tone> = {
  "Incident Report": { bg: "bg-rose-100", text: "text-rose-800" },
  "Help Request": { bg: "bg-purple-100", text: "text-purple-800" },
  "Change Request": { bg: "bg-indigo-100", text: "text-indigo-800" },
  "Feature Request": { bg: "bg-pink-100", text: "text-pink-800" },
  "Incident Request": { bg: "bg-sky-100", text: "text-sky-800" },
  __default: { bg: "bg-gray-200", text: "text-gray-800" },
};

export const typeTone = (t?: string): Tone =>
  TYPE_TONE_MAP[t ?? ""] ?? TYPE_TONE_MAP.__default;
export const TicketTypeTag: React.FC<{ value?: string; compact?: boolean }> = ({
  value,
  compact = true,
}) => {
  const { bg, text, border } = typeTone(value);
  const sizing = compact ? "text-xs px-2 py-0.5" : "text-sm px-2.5 py-1";
  return (
    <span
      className={[
        "inline-flex items-center font-semibold rounded",
        bg,
        text,
        border ?? "",
        sizing,
      ]
        .join(" ")
        .trim()}
    >
      {value ?? "N/A"}
    </span>
  );
};

export const mapTypeToVariant = (
  t?: string
): "success" | "warning" | "error" | "neutral" => {
  switch (t) {
    case "Incident Report":
      return "error";
    case "Help Request":
      return "warning";
    case "Change Request":
      return "neutral";
    case "Feature Request":
      return "success";
    default:
      return "neutral";
  }
};

export const mapPriorityToVariant = (
  p?: number
): "success" | "warning" | "error" | "default" => {
  const label = priorityLabel(p);
  if (label === "Low") return "success";
  if (label === "Medium") return "warning";
  if (label === "High" || label === "Urgent") return "error";
  return "default";
};
// export const TicketTypeTag: React.FC<{ value?: string }> = ({ value }) => {
//   const variant = mapTypeToVariant(value);
//   return <GenericTag variant={variant} label={value ?? "N/A"} size="small" />;
// };

// Optional tiny render helpers (use GenericTag)
export const TicketStatusTag: React.FC<{ value: any }> = ({ value }) => {
  const text = statusText(value);
  const variant = mapStatusToVariant(value);
  return <GenericTag variant={variant} label={text} size="small" />;
};

export const TicketPriorityTag: React.FC<{ value?: number }> = ({ value }) => {
  const label = priorityLabel(value);
  const variant = mapPriorityToVariant(value);
  return <GenericTag variant={variant} label={label} size="small" />;
};

export function makeSupportFields(
  isViewingAllCustomers: boolean,
  isKontrolEdge?: boolean
): FilterField<SupportTicket>[] {
  const fields: FilterField<SupportTicket>[] = [
    {
      label: "Type",
      key: labelId("Type"),
      accessor: (r) => r.type ?? "",
      type: "string",
      inFreeText: true,
    },
    {
      // Works across products: match either key or id
      label: "Ticket Number",
      key: labelId("Ticket Number"),
      accessor: (r) => `${r.key ?? ""} ${r.id ?? ""}`.trim(),
      type: "string",
      inFreeText: true,
    },
    {
      label: "Title",
      key: "subject",
      accessor: (r) => r.subject,
      type: "string",
      inFreeText: true,
    },
    { label: "Reporter",
      key: "reporter",
      accessor: (r) => r.reporterEmail,
      type: "string",
      inFreeText: true
    },
    {
      label: "Status",
      key: labelId("Status"),
      accessor: (r) => statusText(r.status),
      type: "string",
      inFreeText: true,
    },
    {
      // Filter by priority label (High, Medium, Low, Urgent) instead of number
      label: "Priority",
      key: labelId("Priority"),
      accessor: (r) => priorityLabel(r.priority),
      type: "string",
      inFreeText: true,
    },
    {
      label: "Last update",
      key: labelId("Last update"),
      accessor: (r) => {
        if (!r.updatedAt) return "";
        // Extract date part from ISO string (e.g., "2026-02-16T18:05:13.214-0600" -> "2026-02-16")
        const datePart = r.updatedAt.split("T")[0];
        return datePart || "";
      },
      type: "string",
      inFreeText: true,
    }
  ];

  if (isViewingAllCustomers) {
    fields.splice(1, 0, {
      label: "Customer",
      key: "customer",
      accessor: (r) =>
        [r.customer?.name ?? "", r.customer?.chargebeeCustomerId ?? ""]
          .filter(Boolean)
          .join(" "),
      type: "string",
      inFreeText: true,
    });
  }
  
  // Add Service ID field for Kontrol Edge
  if (isKontrolEdge) {
    fields.push({
      label: "Service ID",
      key: labelId("Service ID"),
      accessor: (r) => {
        // Handle service_id as string, array, or null/undefined
        if (!r.service_id) return "";
        if (Array.isArray(r.service_id)) {
          return r.service_id.join(" ");
        }
        if (typeof r.service_id === "string") {
          return r.service_id.replace(/,/g, " ");
        }
        return "";
      },
      type: "string",
      inFreeText: true,
    });
  }

  for (const f of fields) f.key ||= labelId(f.label);
  return fields;
}
